import React from 'react';
import { AlertTriangle, InfoCircle } from '@saleshandy/icons';
import { Button } from 'react-bootstrap';
import { accessibleOnClick } from '../../../../../../../../shared/utils/accessible-on-click';
import {
  TestReportKeySummary,
  SpamAssassinScore,
  TestConfigResponse,
} from '../../../../../../types';
import { InsightKeys, TestType } from '../../../../../../enums/inbox-radar';
import {
  OverlayTooltip,
  Placement,
} from '../../../../../../../../shared/design-system/components/overlay';
import TruncatedTextWithTooltip from '../../../../../../../../shared/components/truncated-text-with-tooltip';
import hasPermission from '../../../../../../../../shared/utils/access-control/has-permission';
import { Permissions } from '../../../../../../../../shared/utils/access-control/enums/permissions';
import planPermissionError from '../../../../../../../../shared/utils/errors/plan-permission-error';
import { Routes } from '../../../../../../../../shared/routes';

const KeySummary: React.FC<{
  keySummaryList: TestReportKeySummary[];
  onSpamAssassinModalOpen?: (
    data: SpamAssassinScore,
    fromKeySummary?: boolean,
  ) => void;
  onEmailAuthenticationModalOpen?: () => void;
  spamAssassinData?: SpamAssassinScore;
  testConfigData?: TestConfigResponse;
  forShareableReport?: boolean;
}> = ({
  keySummaryList,
  onSpamAssassinModalOpen,
  onEmailAuthenticationModalOpen,
  spamAssassinData,
  testConfigData,
  forShareableReport,
}) => {
  const blurCards = [
    {
      key: InsightKeys.SpamScore,
      title: 'Email authentication needs to be set up',
      description:
        'Configure your email account with required authentication settings.Configure your email account with required authentication settings.',
    },
    {
      key: InsightKeys.EmailAuthentication,
      title: 'Email authentication needs to be set up',
      description:
        'Configure your email account with required authentication settings.Configure your email account with required authentication settings.',
    },
    {
      key: InsightKeys.LinksAndAttachments,
      title: 'Email authentication needs to be set up',
      description:
        'Configure your email account with required authentication settings.Configure your email account with required authentication settings. Kindly upgrade your plan.',
    },
  ];
  const getCtaText = (key: string): string => {
    if (
      key === InsightKeys.SpamScore ||
      key === InsightKeys.EmailAuthentication
    ) {
      return 'Review Affected Accounts';
    }
    return 'Update Email Content';
  };

  const handleCtaClick = (key: string) => {
    if (
      key === InsightKeys.SpamScore &&
      spamAssassinData &&
      onSpamAssassinModalOpen
    ) {
      onSpamAssassinModalOpen(spamAssassinData, true);
    } else if (
      key === InsightKeys.EmailAuthentication &&
      onEmailAuthenticationModalOpen
    ) {
      onEmailAuthenticationModalOpen();
    } else {
      let url: string;
      if (testConfigData?.isSubsequence) {
        url = `/sequence/${testConfigData?.parentSequenceId}/subsequence/${testConfigData?.sequenceId}/steps?editVariantId=${testConfigData.variantId}&activeSidebarTab=content-guide&referrer=inboxRadar`;
      } else {
        url = `/sequence/${testConfigData.sequenceId}/steps?editVariantId=${testConfigData.variantId}&activeSidebarTab=content-guide&referrer=inboxRadar`;
      }
      window.open(url, '_blank');
    }
  };

  const handlePlanUpgrade = () => {
    if (!hasPermission(Permissions.INBOX_ANALYZER_TEST_TEMPLATE_KEY_INSIGHTS)) {
      const title = 'Please upgrade to view suggestions';
      const content = [
        "Placement improvement suggestions are a premium feature available on Starter & higher plans. It's not included in your current plan.",
      ];

      planPermissionError.showModal(
        title,
        content,
        true,
        Routes.Setting_Billing_Inbox_Placement_Radar,
      );
    }
  };

  const isPlanExpired =
    keySummaryList?.length === 0 ||
    !hasPermission(Permissions.INBOX_ANALYZER_TEST_TEMPLATE_KEY_INSIGHTS);

  return (
    <div className="test-report-key-summary-container">
      <div className="test-report-key-summary-header">
        <span>Placement Improvement Suggestions</span>
        <OverlayTooltip
          text="Fix these issues to improve your results. Changes made after the test is completed won’t be reflected in this report, run a new test to view updated insights."
          placement={Placement.Bottom}
        >
          <InfoCircle
            width={16}
            height={16}
            className="gray-txt-15 cursor-pointer"
          />
        </OverlayTooltip>
      </div>
      {isPlanExpired && (
        <>
          <div className="test-report-key-summary-plan-upgrade-content">
            <span className="test-report-key-summary-plan-upgrade-header">
              Upgrade to View Suggestions
            </span>
            <span className="test-report-key-summary--plan-upgrade-messsage">
              Placement improvement suggestions are not available on the Free
              plan. Upgrade to Starter or higher plans to unlock actionable
              insights that boost your deliverability.
            </span>
            <Button
              className="test-report-key-summary-plan-upgrade-btn"
              {...accessibleOnClick(handlePlanUpgrade)}
            >
              Upgrade Plan
            </Button>
          </div>
          <div className="locked-wrapper">
            {/* 3 BLUR CARDS */}
            <div className="test-report-key-summary-card-container locked-blur-card-container">
              {blurCards.map((item) => (
                <div
                  key={item.key}
                  className="test-report-key-summary-card blurred-content"
                >
                  <div className="d-flex flex-column gap-12">
                    <div className="test-report-key-summary-card-header">
                      <AlertTriangle width={14} height={14} color="#dc2626" />
                      <span>{item.title}</span>
                    </div>

                    <div className="test-report-key-summary-card-message">
                      <span>{item.description}</span>
                    </div>
                    <div className="test-report-key-summary-card-cta">
                      {getCtaText(item.key)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      <div className="test-report-key-summary-card-container">
        {keySummaryList?.map((item) => (
          <div key={item?.title} className="test-report-key-summary-card">
            <div className="d-flex flex-column gap-12">
              <div className="test-report-key-summary-card-header">
                <AlertTriangle width={14} height={14} color="#dc2626" />
                <span>{item?.title}</span>
              </div>
              {item?.description && (
                <div className="test-report-key-summary-card-message">
                  <span>{item?.description}</span>
                  {item?.children?.length > 0 && (
                    <div className="test-report-key-summary-card-list">
                      <TruncatedTextWithTooltip
                        string={item.children?.join(', ')}
                        length={50}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {item?.key &&
              testConfigData?.type !== TestType.MANUAL_EXTERNAL &&
              !forShareableReport &&
              item?.passed === false &&
              !(
                (testConfigData?.sequenceDeletedAt ||
                  testConfigData?.stepDeletedAt ||
                  testConfigData?.variantDeletedAt) &&
                item?.key !== InsightKeys.SpamScore &&
                item?.key !== InsightKeys.EmailAuthentication
              ) && (
                <div>
                  <span
                    className="test-report-key-summary-card-cta clickable"
                    {...accessibleOnClick(() => handleCtaClick(item.key))}
                  >
                    {getCtaText(item?.key)}
                  </span>
                </div>
              )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default KeySummary;
